import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosProxy } from '../../../api/axiosProxy/axiosProxy';
import { ManifestApiResponse } from '@manifest-cyber/types/interface/apiResponse';
import axios from 'axios';

/* ----------------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------------- */

export interface VulnerabilityAlertConfiguration {
  isActive: boolean;
  alertOnKev: boolean;
  alertOnRecommendation: string[];
  alertOnSeverity: number;
  targetEmails: string[];
}

// API interface for backward compatibility with backend
interface VulnerabilityAlertConfigurationAPI {
  isActive: boolean;
  alertOnKev: boolean;
  alertOnRecommendation: string[];
  alertOnSeverity: number;
  targetEmails: string[];
}

/* ----------------------------------------------------------------------------
 * Hook
 * ------------------------------------------------------------------------- */

export const useVulnerabilityAlertConfig = () => {
  const queryClient = useQueryClient();

  /* ------------------------------ Fetch ---------------------------------- */
  const fetchConfig = useQuery<
    ManifestApiResponse<VulnerabilityAlertConfiguration>,
    Error
  >(['vulnAlertConfig'], async () => {
    try {
      return await AxiosProxy.get({ url: 'vulnerabilities/alert-config' });
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return {
          success: true,
          data: {
            isActive: false,
            alertOnKev: false,
            alertOnRecommendation: [],
            alertOnSeverity: 0,
            targetEmails: [],
          },
        };
      }
      throw error;
    }
  });

  /* ------------------------------ Save ----------------------------------- */
  const saveConfig = useMutation<
    ManifestApiResponse<{ message: string }>,
    Error,
    VulnerabilityAlertConfiguration
  >(
    async (config) => {
      const apiPayload: VulnerabilityAlertConfigurationAPI = {
        ...config,
      };

      return AxiosProxy.post({
        url: 'vulnerabilities/alert-config',
        body: apiPayload,
      });
    },
    {
      onSuccess: () => {
        // Invalidate the query cache to refetch the latest data
        queryClient.invalidateQueries(['vulnAlertConfig']);
      },
    },
  );

  return { fetchConfig, saveConfig };
};
